<template>
  <section class="w-full max-w-[1113px] min-w-[328px] mb-20 custom-quill">
    <form @submit.prevent="createNewComment">
      <div
        v-if="replyOfComment?.id"
        class="relative flex flex-col w-full gap-2 p-3 mb-4 border-l-8 rounded"
        :class="`${
          replyOfComment?.comment
            ? 'bg-h-primary-50 border border-h-primary-100'
            : 'bg-h-neutral-100 border border-h-neutral-200 text-h-neutral-700 font-normal	'
        }`"
      >
        <button
          class="rounded-full flex justify-center items-center absolute top-3 right-3 w-[18px] h-[18px]"
          @click="removeReplyComment()"
        >
          <v-icon
            name="md-close"
            width="16px"
            height="16px"
            class="text-h-neutral-700"
          />
        </button>
        <h6 class="text-h-primary-600 font-extrabold !text-[14px]">
          {{
            replyOfComment?.authorUser?.name
              ? replyOfComment?.authorUser?.name + ":"
              : $t("forum.comment.authorDeleted") + ":"
          }}
        </h6>
        <p
          v-if="replyOfComment?.comment"
          v-html="replyOfComment?.comment"
          class=" ql-editor text-sm text-h-neutral-700 !whitespace-pre-wrap !truncate !line-clamp-3 md:!line-clamp-2 !inline-block !text-wrap"
        ></p>
        
        <span v-else>
          <i>
            {{
              $t("labels.generic", {
                pt: "Comentário excluído.",
                en: "Comment deleted.",
              })
            }}
          </i>
        </span>
      </div>
      <fieldset class="w-[1113px] mx-auto">
        <div class="sm:w-sm md:w-md lg:w-[1008px] xl:w-[1113px]">
          <h5 class="text-xl text-h-neutral-900 leading-7 ml-[2px] mb-[3.8px]">
            {{
              $t("labels.generic", {
                pt: "Adicionar comentário",
                en: "Add comment",
              })
            }}
          </h5>
          <Editor
            v-model="form.comment"
            class="mb-3 w-[93vw] sm:w-[624px] md:w-[752px] lg:w-[992px] xl:w-[1113px] custom-comment-input"
            :class="[
              { 'border !border-red-600 rounded-md': v$.form.comment.$error },
            ]"
            editorStyle="height: 160px; max-width: inherit;"
          >
            <template v-slot:toolbar>
              <div class="ql-formats">
                <button v-tooltip.bottom="'Bold'" class="ql-bold"></button>
                <button v-tooltip.bottom="'Italic'" class="ql-italic"></button>
                <button
                  v-tooltip.bottom="'Underline'"
                  class="ql-underline"
                ></button>
              </div>

              <div class="ql-formats">
                <button
                  v-tooltip.bottom="'Ordered'"
                  class="ql-list"
                  value="ordered"
                ></button>
                <button
                  v-tooltip.bottom="'Bullet'"
                  class="ql-list"
                  value="bullet"
                ></button>
                <select class="ql-align" />
              </div>

              <div class="ql-formats">
                <button
                  v-tooltip.bottom="'Link'"
                  class="ql-link"
                  value="link"
                ></button>
                <button
                  v-tooltip.bottom="'CodeBlock'"
                  class="ql-code-block"
                ></button>
              </div>
            </template>
          </Editor>
          <div class="w-[93vw] sm:w-[624px] md:w-[752px] lg:w-[992px] xl:w-[1113px] custom-comment-input">
            <Errors
              :errors="v$.form.comment.$errors"
              :actualLength="form.comment?.length"
              :maxLength="700"
            />
          </div>
        </div>
      </fieldset>
      <section class="max-w-[1113px] mx-auto">
        <Button v-if="!isImageBeingEmbedded" @click="showEmbedImageComponent" class="!p-0 btn-simple !mb-4 ">
          <v-icon name="hi-upload" class="mr-2" />
          {{
            $t("labels.generic", {
              pt: "Anexar imagem",
              en: "Attach image",
            })
          }}
        </Button>
        <AddFileInput v-else @onSetImage="setCommentImage" @onResetImage="resetCommentImage" :imageSelected="image" />
        <div class="flex items-center">
          <Button type="submit"
            class=" btn-primary transition-all bg-h-primary-500 my-2 px-2 rounded flex items-center justify-center text-white font-bold w-[79px] h-8 box-border"
            :label="$t('labels.generic', { pt: 'Publicar', en: 'Publish' })" />
        </div>
      </section>
    </form>
  </section>
</template>

<script lang="ts">
import { Comment } from "@/models/comment.model";
import { defineAsyncComponent, defineComponent } from "vue";
import { ForumService } from "../../forum.service";
import { GenericsUtils } from "@/utils/generics.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { Messages } from "@/utils/messages.util";
import useVuelidate from "@vuelidate/core";
import { maxLength, required } from "@/locale/locale.config";
import { take } from "rxjs";
import { PropType } from "vue";

const AddFileInput = defineAsyncComponent(
  () => import("@/views/CourseStudent/Forum/components/InputImageFile/input-image-file.vue")
);

export default defineComponent({
  name: "comment-input",
  components: {
    AddFileInput,
  },
  data() {
    return {
      form: new Comment(),
      image: undefined,
      isImageBeingEmbedded: false,
    };
  },
  props: {
    topicId: {
      type: String,
      required: true,
    },
    replyOfComment: {
      type: Object as PropType<Comment>,
      required: true,
    }
  },

  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        comment: { required, max: maxLength(700) },
      },
    };
  },
  methods: {
    createNewComment(): void {
      this.v$.form.$validate();
      if (!this.v$.form.$error) {
        const comment = {
          comment: this.form.comment,
          replyId: this.replyOfComment?.id,
        };

        const appendName: any = { file: "file", data: "comment" };

        const commentForm = this.generics.fileBuild(
          comment,
          this.image,
          appendName
        );
        this.$emit("isSubmitting", true);
        this.saveComment(commentForm);
      }
    },
    saveComment(commentForm: FormData): void {
      this.service.comment.pipe(take(1)).subscribe({
        next: (response) => {
          if (response) {
            this.showToastSuccess();
            this.$emit("commentCreated");
          }
          this.$emit("isSubmitting", false);
        },
      });

      this.service.createComment(this.topicId, commentForm);
    },
    showToastSuccess(): void {
      this.form.comment = "";
      this.resetCommentImage();
      this.removeReplyComment();
      this.v$.$reset();
      this.$toast.add(ToastFactory.success(Messages.FORUM_COMMENT_SAVED));
    },
    setCommentImage(image: any): void {
      this.image = image;
    },
    resetCommentImage(): void {
      this.image = undefined;
    },
    showEmbedImageComponent(): void {
      this.isImageBeingEmbedded = true;
    },
    removeReplyComment(): void {
      this.$emit("removeReplyComment");
    },
  },
  computed: {
    service(): ForumService {
      return new ForumService();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
  },
});
</script>

